import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import RefreshIcon from '@material-ui/icons/Refresh';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    margin: theme.spacing(2, 0),
  },
  countdown: {
    fontSize: '0.75rem',
    fontWeight: 300,
    color: theme.palette.text.secondary,
    minWidth: '80px',
    lineHeight: 1,
    marginLeft: theme.spacing(1),
  },
  formControlLabel: {
    margin: 0,
  },
}));

export default function AutoRefresh({ onRefresh, disabled = false, interval = 10000, disableAuto = false, autoSyncDefault = false }) {
  const classes = useStyles();
  const [autoSync, setAutoSync] = useState(autoSyncDefault);
  const [countdown, setCountdown] = useState(interval / 1000);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (autoSync && !disabled) {
      // Start the auto-refresh timer
      timerRef.current = setInterval(async () => {
        await handleRefresh();
      }, interval);

      // Start the countdown timer (updates every second)
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            return interval / 1000;
          }
          return prev - 1;
        });
      }, 1000);

      // Set initial countdown
      setCountdown(interval / 1000);
    } else {
      // Clear timers when auto-sync is disabled
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      setCountdown(interval / 1000);
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [autoSync, disabled, interval, onRefresh]);

  const handleRefresh = async () => {
    if (isRefreshing || disabled) return;
    
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
      // Reset countdown after manual refresh
      if (autoSync) {
        setCountdown(interval / 1000);
      }
    }
  };

  const handleToggleAutoSync = () => {
    setAutoSync(prev => !prev);
  };

  return (
    <Box className={classes.container}>
      <Button
        color="primary"
        onClick={handleRefresh}
        disabled={isRefreshing || disabled}
        startIcon={<RefreshIcon />}
      >
        {isRefreshing ? 'Opdaterer......' : 'Opdater indhold'}
      </Button>
      
      {!disableAuto && (
        <>
          <Button
            variant={autoSync ? "contained" : "outlined"}
            color="primary"
            onClick={handleToggleAutoSync}
            disabled={disabled}
          >
            Auto-sync
          </Button>
          
          {autoSync && !disabled && (
            <Typography className={classes.countdown}>
              Næste om {countdown}s
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
